
import React from 'react';
import { MediaType } from '../types';
import { FilterIcon, GridIcon, ListIcon, ChevronDownIcon } from './icons';

interface FilterBarProps {
  mediaTypeFilter: string;
  setMediaTypeFilter: (value: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  viewMode: 'grid' | 'list';
  setViewMode: (mode: 'grid' | 'list') => void;
  sortBy: string;
  setSortBy: (value: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  mediaTypeFilter,
  setMediaTypeFilter,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
  sortBy,
  setSortBy,
}) => {
  return (
    <div
      className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 w-full"
      data-tutorial-id="filter-bar"
    >
      {/* Top Row on Mobile, Inline on Desktop: Filters */}
      <div className="flex items-center gap-2 w-full lg:w-auto shrink-0">
        <FilterIcon className="flex-shrink-0 hidden sm:block text-text-faint" />
        <div className="relative flex-1 sm:w-36 sm:flex-none">
          <select
            value={mediaTypeFilter}
            onChange={(e) => setMediaTypeFilter(e.target.value)}
            className={`appearance-none w-full border rounded-lg py-2 pl-3 pr-8 text-sm focus:outline-none focus:border-action ${mediaTypeFilter !== 'all' ? 'bg-action-soft border-action text-action' : 'bg-white border-app-border text-text-ink'}`}
          >
            <option value="all">All Media</option>
            <option value={MediaType.Photo}>Photos</option>
            <option value={MediaType.Video}>Videos</option>
            <option value={MediaType.Screen}>Screen Recordings</option>
            <option value={MediaType.Audio}>Audio</option>
            <option value={MediaType.Signification}>Signification</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
            <ChevronDownIcon className={`w-4 h-4 ${mediaTypeFilter !== 'all' ? 'text-action' : 'text-text-faint'}`} />
          </div>
        </div>
        <div className="relative flex-1 sm:w-44 sm:flex-none">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`appearance-none w-full border rounded-lg py-2 pl-3 pr-8 text-sm focus:outline-none focus:border-action ${sortBy !== 'date-desc' ? 'bg-action-soft border-action text-action' : 'bg-white border-app-border text-text-ink'}`}
            aria-label="Sort submissions"
          >
            <option value="date-desc">Date: Newest</option>
            <option value="date-asc">Date: Oldest</option>
            <option value="participant-asc">Participant Name</option>
            <option value="tags-asc">Tags</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
            <ChevronDownIcon className={`w-4 h-4 ${sortBy !== 'date-desc' ? 'text-action' : 'text-text-faint'}`} />
          </div>
        </div>
      </div>

      {/* Bottom Row on Mobile, Inline on Desktop: Search + View Toggles */}
      <div className="flex items-center gap-2 w-full lg:flex-1 min-w-0">
        <input
          type="text"
          placeholder="Search by name, note, tag..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="bg-white border border-app-border rounded-lg py-2 px-3 text-sm flex-1 min-w-0 focus:outline-none focus:border-action"
        />
        <div className="flex items-center justify-center gap-2 flex-shrink-0">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md border transition-all active:scale-90 ${viewMode === 'list'
              ? 'bg-app-bg border-app-border text-text-ink shadow-sm'
              : 'bg-white border-transparent text-text-faint hover:bg-app-bg hover:text-text-ink'
              }`}
            aria-pressed={viewMode === 'list'}
            aria-label="Switch to list view"
          >
            <ListIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md border transition-all active:scale-90 ${viewMode === 'grid'
              ? 'bg-app-bg border-app-border text-text-ink shadow-sm'
              : 'bg-white border-transparent text-text-faint hover:bg-app-bg hover:text-text-ink'
              }`}
            aria-pressed={viewMode === 'grid'}
            aria-label="Switch to grid view"
          >
            <GridIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
